import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Lightbulb, Volume2, UserCheck, GraduationCap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Teacher Instruction Banner ──────────────────────────────────
const TEACHER_GUIDANCE = {
  k_labeling: {
    banner: "Teacher: Please read the prompt aloud to the student. Help them identify words in the picture. Assist with typing if needed.",
    studentHint: "Your teacher will help you!",
    color: "bg-violet-50 border-violet-300 text-violet-800",
    iconColor: "text-violet-600",
  },
  k_sentence_completion: {
    banner: "Teacher: Read the sentence aloud. Ask the student what word completes it. Type or help the student type their answer.",
    studentHint: "Listen to your teacher, then fill in the blank!",
    color: "bg-violet-50 border-violet-300 text-violet-800",
    iconColor: "text-violet-600",
  },
  g1_sentences: {
    banner: "Teacher: Read the prompt and sentence starters aloud. Encourage the student to write their own words. Assist with spelling as needed.",
    studentHint: "Your teacher will read the question. Try your best to write!",
    color: "bg-blue-50 border-blue-300 text-blue-800",
    iconColor: "text-blue-600",
  },
  g2_short_narrative: {
    banner: "Teacher: Read the prompt aloud. Guide the student through the sentence starters. Students should write independently with spelling support as needed.",
    studentHint: "Listen to the question, then write your answer using the hints below.",
    color: "bg-sky-50 border-sky-300 text-sky-800",
    iconColor: "text-sky-600",
  },
};

const TeacherBanner = ({ questionType, isK2 }) => {
  const guidance = TEACHER_GUIDANCE[questionType];
  if (!guidance) return null;

  return (
    <div className={`border-2 p-4 mb-4 ${guidance.color}`} data-testid="teacher-banner">
      {/* Teacher instruction */}
      <div className="flex items-start gap-3 mb-2">
        <UserCheck size={isK2 ? 24 : 20} className={`flex-shrink-0 mt-0.5 ${guidance.iconColor}`} />
        <div>
          <div className={`font-bold ${isK2 ? "text-base" : "text-sm"}`}>
            {guidance.banner}
          </div>
        </div>
      </div>
      {/* Student-facing hint */}
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-current/10">
        <GraduationCap size={isK2 ? 20 : 16} className={guidance.iconColor} />
        <span className={`font-semibold ${isK2 ? "text-lg" : "text-sm"}`}>
          {guidance.studentHint}
        </span>
      </div>
    </div>
  );
};

// ── Auto-Read TTS Button ────────────────────────────────────────
const ReadAloudButton = ({ text, isK2 }) => {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  const handlePlay = async () => {
    // Stop if already playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/audio/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, voice: "nova", speed: isK2 ? 0.9 : 0.95 }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.audio) {
          const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
          audioRef.current = audio;
          audio.onplay = () => setPlaying(true);
          audio.onended = () => { setPlaying(false); audioRef.current = null; };
          audio.onerror = () => { setPlaying(false); audioRef.current = null; };
          audio.play().catch(() => setPlaying(false));
        }
      } else {
        // Fallback to browser TTS
        fallbackBrowserTTS(text, isK2, setPlaying);
      }
    } catch {
      fallbackBrowserTTS(text, isK2, setPlaying);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, []);

  return (
    <button
      onClick={handlePlay}
      disabled={loading}
      className={`inline-flex items-center gap-2 font-bold transition-colors ${
        isK2
          ? "px-5 py-3 text-lg bg-[#002FA7] text-white hover:bg-[#001f7a]"
          : "px-4 py-2 text-sm bg-[#002FA7] text-white hover:bg-[#001f7a]"
      } ${loading ? "opacity-50" : ""}`}
      data-testid="read-aloud-btn"
    >
      <Volume2 size={isK2 ? 22 : 16} className={playing ? "animate-pulse" : ""} />
      {loading ? "Loading..." : playing ? "Playing..." : isK2 ? "Read to Me" : "Read Aloud"}
    </button>
  );
};

const fallbackBrowserTTS = (text, isK2, setPlaying) => {
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = isK2 ? 0.85 : 0.95;
    u.onstart = () => setPlaying(true);
    u.onend = () => setPlaying(false);
    window.speechSynthesis.speak(u);
  }
};

// ══════════════════════════════════════════════════════════════════
// Main WritingInput Component
// ══════════════════════════════════════════════════════════════════
const WritingInput = ({ question, value, onChange, isK2, isMid, spellCheck = true }) => {
  const qType = question.question_type;
  const wordBank = question.word_bank || [];
  const starters = question.sentence_starters || [];
  const bpImage = question.bp_image_url;

  if (qType === "k_labeling") {
    return (
      <div>
        <TeacherBanner questionType={qType} isK2={true} />
        <KLabelingInput question={question} value={value} onChange={onChange} wordBank={wordBank} bpImage={bpImage} spellCheck={spellCheck} />
      </div>
    );
  }

  if (qType === "k_sentence_completion") {
    return (
      <div>
        <TeacherBanner questionType={qType} isK2={true} />
        <KSentenceCompletion question={question} value={value} onChange={onChange} wordBank={wordBank} bpImage={bpImage} spellCheck={spellCheck} />
      </div>
    );
  }

  if (qType === "g1_sentences") {
    return (
      <div>
        <TeacherBanner questionType={qType} isK2={true} />
        <G1SentenceInput question={question} value={value} onChange={onChange} wordBank={wordBank} starters={starters} bpImage={bpImage} spellCheck={spellCheck} />
      </div>
    );
  }

  if (qType === "g2_short_narrative") {
    return (
      <div>
        <TeacherBanner questionType={qType} isK2={false} />
        <G2GuidedParagraph question={question} value={value} onChange={onChange} wordBank={wordBank} starters={starters} spellCheck={spellCheck} />
      </div>
    );
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════
// K: Label / Word Writing
// ═══════════════════════════════════════════════════════════════
const KLabelingInput = ({ question, value, onChange, wordBank, bpImage, spellCheck = true }) => {
  const [labels, setLabels] = useState(() => {
    try { return JSON.parse(value) || ["", "", ""]; }
    catch { return ["", "", ""]; }
  });

  const updateLabel = (idx, text) => {
    const updated = [...labels];
    updated[idx] = text;
    setLabels(updated);
    onChange(JSON.stringify(updated));
  };

  const addWordFromBank = (word) => {
    const emptyIdx = labels.findIndex((l) => l === "");
    if (emptyIdx >= 0) updateLabel(emptyIdx, word);
  };

  return (
    <div className="space-y-5" data-testid="k-labeling-input">
      {/* Read Aloud + Image */}
      <div className="flex items-center gap-3 mb-2">
        <ReadAloudButton text={question.question_text} isK2={true} />
      </div>

      {bpImage && (
        <div className="border-2 border-gray-200 overflow-hidden">
          <img src={bpImage} alt="Writing prompt" className="w-full max-h-[400px] object-contain bg-gray-50" data-testid="writing-bp-image" />
        </div>
      )}

      {/* Label boxes */}
      <div className="space-y-3">
        <p className="text-lg font-bold text-gray-700">Write the words you see:</p>
        <div className="grid grid-cols-3 gap-3">
          {labels.map((label, i) => (
            <div key={i} className="relative">
              <div className="text-xs text-gray-400 font-bold mb-1">WORD {i + 1}</div>
              <input
                type="text"
                value={label}
                onChange={(e) => updateLabel(i, e.target.value)}
                placeholder="type a word"
                spellCheck={spellCheck}
                className="w-full h-14 text-2xl font-bold text-center border-2 border-gray-300 focus:border-[#002FA7] focus:outline-none bg-white"
                data-testid={`k-label-input-${i}`}
              />
            </div>
          ))}
        </div>
      </div>

      <WordBank words={wordBank} onWordClick={addWordFromBank} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// K: Sentence Completion
// ═══════════════════════════════════════════════════════════════
const KSentenceCompletion = ({ question, value, onChange, wordBank, bpImage, spellCheck = true }) => {
  const parts = question.question_text.split("___");
  const beforeBlank = parts[0]?.split(": ").pop() || "";
  const afterBlank = parts[1] || ".";

  return (
    <div className="space-y-5" data-testid="k-sentence-completion">
      <div className="flex items-center gap-3 mb-2">
        <ReadAloudButton text={question.question_text} isK2={true} />
      </div>

      {bpImage && (
        <div className="border-2 border-gray-200 overflow-hidden">
          <img src={bpImage} alt="Writing prompt" className="w-full max-h-[400px] object-contain bg-gray-50" data-testid="writing-bp-image" />
        </div>
      )}

      <div className="bg-blue-50 border-2 border-blue-200 p-5">
        <p className="text-lg font-bold text-gray-700 mb-3">Finish the sentence:</p>
        <div className="flex items-center gap-2 flex-wrap text-2xl font-bold text-gray-800">
          <span>{beforeBlank}</span>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="___"
            spellCheck={spellCheck}
            className="w-48 h-12 text-2xl font-bold text-center border-b-4 border-[#002FA7] bg-transparent focus:outline-none"
            data-testid="k-sentence-blank"
          />
          <span>{afterBlank}</span>
        </div>
      </div>

      <WordBank words={wordBank} onWordClick={(w) => onChange(w)} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Grade 1: Sentence Writing
// ═══════════════════════════════════════════════════════════════
const G1SentenceInput = ({ question, value, onChange, wordBank, starters, bpImage, spellCheck = true }) => {
  const [sentences, setSentences] = useState(() => {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : (starters.length > 0 ? starters.map(() => "") : ["", ""]);
    } catch {
      return starters.length > 0 ? starters.map(() => "") : ["", ""];
    }
  });

  const updateSentence = (idx, text) => {
    const updated = [...sentences];
    updated[idx] = text;
    setSentences(updated);
    onChange(JSON.stringify(updated));
  };

  return (
    <div className="space-y-5" data-testid="g1-sentence-input">
      <div className="flex items-center gap-3 mb-2">
        <ReadAloudButton text={question.question_text} isK2={true} />
      </div>

      {bpImage && (
        <div className="border-2 border-gray-200 overflow-hidden">
          <img src={bpImage} alt="Writing prompt" className="w-full max-h-[400px] object-contain bg-gray-50" data-testid="writing-bp-image" />
        </div>
      )}

      <div className="space-y-3">
        {starters.length > 0 ? (
          starters.map((starter, i) => {
            const parts = starter.split("___");
            return (
              <div key={i} className="bg-white border-2 border-gray-200 p-4">
                <div className="text-xs text-gray-400 font-bold mb-2">SENTENCE {i + 1}</div>
                <div className="flex items-start gap-1 flex-wrap">
                  <span className="text-lg font-semibold text-gray-700 pt-1">{parts[0]}</span>
                  <input
                    type="text"
                    value={sentences[i] || ""}
                    onChange={(e) => updateSentence(i, e.target.value)}
                    placeholder="write here"
                    spellCheck={spellCheck}
                    className="flex-1 min-w-[200px] h-10 text-lg border-b-2 border-gray-300 focus:border-[#002FA7] bg-transparent focus:outline-none px-1"
                    data-testid={`g1-sentence-input-${i}`}
                  />
                  {parts[1] && <span className="text-lg font-semibold text-gray-700 pt-1">{parts[1]}</span>}
                </div>
              </div>
            );
          })
        ) : (
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-white border-2 border-gray-200 p-4">
              <div className="text-xs text-gray-400 font-bold mb-2">SENTENCE {i + 1}</div>
              <input
                type="text"
                value={sentences[i] || ""}
                onChange={(e) => updateSentence(i, e.target.value)}
                placeholder="Write a sentence here..."
                spellCheck={spellCheck}
                className="w-full h-10 text-lg border-b-2 border-gray-300 focus:border-[#002FA7] bg-transparent focus:outline-none"
                data-testid={`g1-sentence-input-${i}`}
              />
            </div>
          ))
        )}
      </div>

      <WordBank words={wordBank} onWordClick={() => {}} label="Word Bank — use these words to help you write:" />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Grade 2: Guided Paragraph
// ═══════════════════════════════════════════════════════════════
const G2GuidedParagraph = ({ question, value, onChange, wordBank, starters, spellCheck = true }) => {
  return (
    <div className="space-y-5" data-testid="g2-guided-paragraph">
      <div className="flex items-center gap-3 mb-2">
        <ReadAloudButton text={question.question_text} isK2={false} />
      </div>

      {starters.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-4">
          <div className="flex items-center gap-2 text-amber-700 font-bold text-sm mb-2">
            <Lightbulb size={16} />
            Try starting your sentences like this:
          </div>
          <ul className="space-y-1">
            {starters.map((s, i) => (
              <li key={i} className="text-amber-800 text-sm italic">{s}</li>
            ))}
          </ul>
        </div>
      )}

      <Textarea
        placeholder="Write your sentences here..."
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={spellCheck}
        className="min-h-[160px] text-lg border-2 border-gray-300 focus:border-[#002FA7] rounded-none resize-none p-4 leading-relaxed"
        data-testid="g2-writing-textarea"
      />

      <WordBank words={wordBank} onWordClick={() => {}} label="Word Bank — use these words to help you write:" />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Word Bank (shared)
// ═══════════════════════════════════════════════════════════════
const WordBank = ({ words, onWordClick, label }) => {
  if (!words || words.length === 0) return null;
  return (
    <div className="bg-green-50 border-2 border-green-200 p-4" data-testid="word-bank">
      <div className="text-sm font-bold text-green-800 mb-2">
        {label || "WORD BANK — click a word to use it:"}
      </div>
      <div className="flex flex-wrap gap-2">
        {words.map((word, i) => (
          <button
            key={i}
            onClick={() => onWordClick(word)}
            className="px-3 py-1.5 bg-white border-2 border-green-300 text-green-800 font-bold text-lg hover:bg-green-100 hover:border-green-500 transition-colors cursor-pointer"
            data-testid={`word-bank-${i}`}
          >
            {word}
          </button>
        ))}
      </div>
    </div>
  );
};

export default WritingInput;
